import { Link } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { m } from "motion/react";
import { useCallback, useMemo, useState } from "react";

import { Icon } from "../components/icon";
import { menuAnimation } from "../lib/const";
import { useGlobal } from "../lib/global-context";
import { routeStore } from "../lib/store";
import type { VoidComponent } from "../lib/types";
import { booleanAttribute } from "../lib/utils";

interface Props {
	params: { pillar: string; category: string };
}

const MobileBreadcrumbs: VoidComponent<Props> = ({ params }) => {
	const { level } = useStore(routeStore);

	const { pillars } = useGlobal();

	const [isCopied, setIsCopied] = useState(false);

	const pillar = useMemo(
		() => pillars.find((p) => p.slug.current === params.pillar),
		[pillars, params.pillar],
	);

	const category = useMemo(
		() => pillar?.categories.find((c) => c.slug.current === params.category),
		[pillar?.categories, params.category],
	);

	const pillarParams = useMemo(() => ({ pillar: params.pillar }), [params.pillar]);
	const categoryParams = useMemo(
		() => ({ pillar: params.pillar, category: params.category }),
		[params.pillar, params.category],
	);

	const handleCopyLink = useCallback(() => {
		navigator.clipboard
			.writeText(globalThis.location.href)
			.then(() => {
				setIsCopied(true);
				setTimeout(() => {
					setIsCopied(false);
				}, 2000);
				return null;
			})
			.catch(console.error);
	}, []);

	return (
		<m.nav
			{...menuAnimation(true)}
			className="flex min-h-12 items-center justify-between px-4 desktop:hidden"
		>
			<div className="flex min-w-0 flex-1 items-center gap-2">
				{level === "category" && (
					<Link
						to="/$pillar"
						params={pillarParams}
						className="flex min-w-0 items-center text-xs text-foreground"
					>
						<Icon name="nav-arrow-left" className="size-5 shrink-0" />

						<span className="truncate">{pillar?.title}</span>
					</Link>
				)}

				{level === "initiative" && (
					<Link
						to="/$pillar/$category"
						params={categoryParams}
						className="flex min-w-0 items-center text-xs text-foreground"
					>
						<Icon name="nav-arrow-left" className="size-5 shrink-0" />

						<span className="truncate">{category?.title}</span>
					</Link>
				)}
			</div>

			<button
				type="button"
				onClick={handleCopyLink}
				className="group/copy-link flex shrink-0 cursor-pointer items-center gap-1 rounded-none border-transparent px-2 py-1 text-foreground transition-all duration-200"
				data-is-copied={booleanAttribute(isCopied)}
			>
				<span className="text-xs text-foreground underline opacity-0 transition-opacity duration-200 ease-out group-hover/copy-link:text-foreground group-hover/copy-link:opacity-100 group-data-is-copied/copy-link:opacity-100">
					{isCopied ? "copied link" : "copy link"}
				</span>

				<Icon name="link" className="size-5 shrink-0" />
			</button>
		</m.nav>
	);
};

export { MobileBreadcrumbs };
